import React, { useRef } from 'react';
import { AlertCircle, RefreshCw, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { logger } from '@/lib/procurement/logger';
import { useNavigate } from 'react-router-dom';

enum ErrorCategory {
  NETWORK = 'NETWORK',
  AUTH = 'AUTH',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
}

interface ProcurementErrorBoundaryProps {
  children: React.ReactNode;
  onRetry?: () => void;
  fallbackMessage?: string;
}

function categorizeError(error: Error): ErrorCategory {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  if (message.includes('unauthorized') || message.includes('403') || name.includes('auth')) {
    return ErrorCategory.AUTH;
  }
  if (message.includes('network') || message.includes('fetch') || name.includes('network')) {
    return ErrorCategory.NETWORK;
  }
  if (message.includes('server') || message.includes('50') || name.includes('server')) {
    return ErrorCategory.SERVER;
  }
  return ErrorCategory.CLIENT;
}

function getExponentialBackoffDelay(attempt: number): number {
  return Math.min(Math.pow(2, attempt) * 1000, 4000); // 1s, 2s, 4s max
}

const ProcurementErrorFallback: React.FC<{
  onRetry?: () => void;
  fallbackMessage?: string;
  error?: Error;
}> = ({ onRetry, fallbackMessage, error }) => {
  const navigate = useNavigate();
  const attemptRef = useRef(0);

  const category = error ? categorizeError(error) : ErrorCategory.CLIENT;

  const handleAuthError = () => {
    logger.info('USER_ACTION', 'Auth error - redirecting to login');
    navigate('/auth/login');
  };

  // 🪦 PR-G3: circuitBreaker flag retired — fallback path was unlimited retry in prod.
  const isRetryBlocked = (): boolean => false;

  const handleRetry = async () => {
    if (category === ErrorCategory.AUTH) {
      handleAuthError();
      return;
    }

    if (isRetryBlocked()) {
      logger.warn('USER_ACTION', 'Retry blocked');
      return;
    }

    try {
      const delay = getExponentialBackoffDelay(attemptRef.current);
      logger.info('USER_ACTION', `Retrying after ${delay}ms delay, attempt ${attemptRef.current + 1}`);
      await new Promise((resolve) => setTimeout(resolve, delay));
      attemptRef.current = 0;
      onRetry?.();
    } catch (retryError) {
      attemptRef.current++;
      logger.error('ERROR_BOUNDARY', retryError);
    }
  };

  const handleReload = () => {
    logger.info('USER_ACTION', 'Error boundary page reload clicked');
    window.location.reload();
  };

  const getErrorMessage = (): string => {
    if (fallbackMessage) return fallbackMessage;
    switch (category) {
      case ErrorCategory.AUTH:
        return 'Bạn không có quyền truy cập. Hệ thống sẽ chuyển về trang đăng nhập.';
      case ErrorCategory.NETWORK:
        return 'Lỗi kết nối mạng. Vui lòng kiểm tra kết nối và thử lại.';
      case ErrorCategory.SERVER:
        return 'Máy chủ đang gặp sự cố. Vui lòng thử lại sau ít phút.';
      default:
        return 'Có lỗi xảy ra với module Procurement. Vui lòng thử lại.';
    }
  };

  return (
    <Alert className="mx-auto max-w-md">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        <div className="space-y-3">
          <p>{getErrorMessage()}</p>
          <div className="flex gap-2">
            {category === ErrorCategory.AUTH ? (
              <Button variant="outline" size="sm" onClick={handleAuthError} className="flex items-center gap-2">
                <LogOut className="h-4 w-4" />
                Đăng nhập lại
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={handleRetry} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Thử lại
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleReload} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Tải lại trang
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ProcurementErrorBoundary extends React.Component<ProcurementErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ProcurementErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('ERROR_BOUNDARY', 'Procurement error boundary caught', { error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ProcurementErrorFallback
          onRetry={this.handleRetry}
          fallbackMessage={this.props.fallbackMessage}
          error={this.state.error}
        />
      );
    }
    return this.props.children;
  }
}
